import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Moment from "moment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { FaPlus } from "react-icons/fa";
import AuthContext from "../../../auth/AuthContext";
import SupportService from "../../../services/SupportService";
import AlertService from "../../../services/AlertService";
import View from "./View";

const Support = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [status, setStatus] = useState(0);
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(20);
  const [pageNo, setPageNo] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [maxPageNo, setMaxPageNo] = useState(1);
  const pageOptions = [ 20, 50, 100];
  const authContext = useContext(AuthContext);
  const [tableScrollHeight, setTableScrollHeight] = useState('400px');

  const calculateScrollHeight = () => {
    const windowHeight = window.innerHeight;
    let bannerHeight = 150;
    const otherFixedElementsHeight = 135;

    let calculatedHeight = windowHeight - bannerHeight - otherFixedElementsHeight;
    if (calculatedHeight < 200) {
      calculatedHeight = 200;
    }
    setTableScrollHeight(`${calculatedHeight}px`);
  };

  useEffect(() => {
    calculateScrollHeight();
    window.addEventListener('resize', calculateScrollHeight);
    return () => {
      window.removeEventListener('resize', calculateScrollHeight);
    };
  }, []);

  useEffect(() => {
    const supportAccess = authContext.getPermissions("support");
    if (supportAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
  }, [authContext, navigate]);

const loadTickets = useCallback(async (signal) => {
    setLoading(true);
    setTicket(null);
    try {
      const data = await SupportService.GetAll(
        status,
        skip,
        take,
        authContext,
        signal 
      );

      if (signal?.aborted) return;

      if (data.isSuccess) {
        setTotalRecords(data.data.totalRecords);
        let mPageNo = Math.ceil(data.data.totalRecords / take);
        setMaxPageNo(mPageNo === 0 ? 1 : mPageNo);
        setTickets(data.data.records);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [status, skip, take, authContext]);

useEffect(() => {
    const controller = new AbortController();
    loadTickets(controller.signal);
    return () => controller.abort();
  }, [loadTickets, status, skip, take, authContext]);

  const changeStatus = (value) => {
  setStatus(value);
  setPageNo(1);
  setSkip(0);
};

  const goToPreviousPage = () => {
    let cPageNo = pageNo - 1;
    cPageNo = cPageNo > 0 ? cPageNo : 1;
    setPageNo(cPageNo);
    setSkip(cPageNo * take - take);
  };

  const goToNextPage = () => {
    let cPageNo = pageNo + 1;
    cPageNo = cPageNo <= maxPageNo ? cPageNo : maxPageNo;
    setPageNo(cPageNo);
    setSkip(cPageNo * take - take);
  };

  const statusBodyTemplate = (rowData) => {
    if (loading) return <Skeleton width="80px" height="25px" />;
    let color = "";
    switch (rowData.statusDesc) {
      case "In-Progress":
        color = "#0697DF";
        break;
      case "Closed":
        color = "#017319";
        break;
      case "Pending":
        color = "#FF5911";
        break;
      case "Cancelled":
        color = "#D41B1B";
        break;
      default:
        color = "#6c757d";
    }

    return <span style={{ color: color }}>{rowData.statusDesc}</span>;
  };

  const dateBodyTemplate = (rowData) => {
    if (loading) return <Skeleton width="100px" height="25px" />;

    return Moment.utc(rowData.ticketDate)
      .local()
      .format("DD/MM/YYYY hh:mm A");
  };

const textSkeleton = (width = "100px", height = "25px") => (
  <Skeleton width={width} height={height} />
);


  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (maxPageNo <= 5) {
      for (let i = 1; i <= maxPageNo; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (pageNo > maxVisiblePages + 1) {
        pages.push("...");
      }

      let start = Math.max(2, pageNo - 1);
      let end = Math.min(maxPageNo - 1, pageNo + 1);

      if (pageNo <= maxVisiblePages) end = 4;
      else if (pageNo > maxPageNo - maxVisiblePages) start = maxPageNo - 3;

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (pageNo < maxPageNo - maxVisiblePages) {
        pages.push("...");
      }

      if (maxPageNo > 1) pages.push(maxPageNo);
    }

    return pages;
  };

  const getPlaceholderRows = () => {
    return Array.from({ length: take }, (_, i) => ({ id: `skeleton-${i}` }));
  };

  return (
    <div className="container-fluid custom-container position-relative pb-3">
      {!ticket ? (
        <>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mt-3">
            <div className="d-flex">
              <button className="btn all-btn p-2" onClick={() => changeStatus(0)}>All</button>
              <button className="btn pending-btn p-2" onClick={() => changeStatus(1)}>Pending</button>
              <button className="btn inprogress-btn p-2" onClick={() => changeStatus(2)}>In-Progress</button>
              <button className="btn closed-btn p-2" onClick={() => changeStatus(3)}>Closed</button>
              <button className="btn cancelled-btn p-2" onClick={() => changeStatus(4)}>Cancelled</button>
            </div>
            <div>
              <button className="btn btn-primary px-3 py-2" onClick={() => navigate("/support/add")}>
                <FaPlus className="pb-1 me-1" /> Add Ticket
              </button>
            </div>
          </div>

          <DataTable
            scrollable scrollHeight={tableScrollHeight}
            value={loading ? getPlaceholderRows() : tickets}
            className="custom-table"
            style={{ cursor: 'pointer' }}
            onRowClick={(e) => {
              if (!loading && e.data) setTicket(e.data);
            }}
            rowClassName={(data) => (ticket?.id === data.id ? "selected-row" : "")}
          >
           <Column
  header="S. No."
  body={(_rowData, options) =>
    loading ? textSkeleton("30px") : ((pageNo - 1) * take) + options.rowIndex + 1
  }
  style={{ width: "20px", textAlign: "start" }}
/>

            <Column
              field="ticketId"
              header="Ticket ID"
              body={(rowData) => loading ? textSkeleton("80px") : rowData.ticketId}
              style={{ width: "200px" }}
            />
            <Column
              field="courseName"
              header="Course"
              body={(rowData) => loading ? textSkeleton("200px") : rowData.courseName}
              style={{ width: "250px" }}
            />
            <Column
              field="subject"
              header="Subject"
              body={(rowData) => loading ? textSkeleton("200px") : rowData.subject}
              style={{ width: "250px" }}
            />
            <Column
              field="statusDesc"
              header="Status"
              body={statusBodyTemplate}
              style={{ width: "150px" }}
            />
            <Column
              field="ticketDate"
              header="Request Date"
              body={dateBodyTemplate}
              style={{ width: "150px" }}
            />
          </DataTable>

          <div className="d-flex flex-column flex-start flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
            <div className="d-flex align-items-center gap-3">
                <label className="">Show Rows:</label>
                <select
                  className="form-select form-select-sm w-auto text-secondary custom-input-color"
                  value={take}
                  onChange={(e) => {
                    setTake(parseInt(e.target.value));
                    setPageNo(1);
                    setSkip(0);
                  }}
                >
                  {pageOptions.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                            <span >
                Total Records: <span className="text-secondary">{totalRecords}</span>
              </span>
            </div>


            <div className="d-flex justify-content-center align-items-center gap-2">
              <button
                className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={goToPreviousPage}
                disabled={pageNo === 1}
              >
                &lt;
              </button>

              {generatePageNumbers().map((page, index) =>
                page === "..." ? (
                  <span key={index} className="mx-1 text-secondary">...</span>
                ) : (
                  <button
                    key={index}
                    className={`btn p-0 d-flex align-items-center justify-content-center text-secondary pagination-item ${pageNo === page ? "active-page" : ""
                      }`}
                    style={{ width: "40px", height: "40px", transition: "all 0.2s" }}
                    onClick={() => {
                      setPageNo(page);
                      setSkip(page * take - take);
                    }}
                  >
                    {page}
                  </button>
                )
              )}

              <button
                className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={goToNextPage}
                disabled={pageNo >= maxPageNo}
              >
                &gt;
              </button>
            </div>
          </div>
        </>
      ) : (
        <View
          ticket={ticket}
          clearSelection={() => setTicket(null)}
        />
      )}
    </div>
  );
};

export default Support;
